import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Brain, Check, Pencil, Plus, Trash2, X, ThumbsUp, ThumbsDown } from "lucide-react";

function Regle({ r, editable, validable, onChange }) {
  const [edit, setEdit] = useState(false);
  const [texte, setTexte] = useState(r.texte);
  const run = async (fn, ok) => { try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); } };
  return (
    <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2" data-testid={`memoire-regle-${r.id}`}>
      <div className="flex-1 min-w-0">
        {edit ? <Input value={texte} onChange={(e) => setTexte(e.target.value)} className="h-8 text-sm" data-testid={`memoire-edit-input-${r.id}`} />
          : <div className="text-sm text-slate-800">{r.texte}</div>}
        <div className="text-[11px] text-slate-400 mt-0.5">{r.auteur} · {new Date(r.created_at).toLocaleDateString("fr-FR")} · {r.source === "correction" ? "correction retenue" : r.source === "consigne" ? "consigne" : "ajout manuel"}{r.validee_par ? ` · validée par ${r.validee_par}` : ""}</div>
      </div>
      {validable && <>
        <Button size="sm" className="h-7 bg-emerald-700 hover:bg-emerald-800" onClick={() => run(() => api.patch(`/zoe/memoire/${r.id}`, { statut: "active" }), "Règle validée pour tout le GE")} data-testid={`memoire-valider-${r.id}`}><Check className="h-3 w-3 mr-1" />Valider</Button>
        <Button size="sm" variant="outline" className="h-7" onClick={() => run(() => api.patch(`/zoe/memoire/${r.id}`, { statut: "rejetee" }), "Proposition rejetée")} data-testid={`memoire-rejeter-${r.id}`}><X className="h-3 w-3 mr-1" />Rejeter</Button>
      </>}
      {editable && (edit
        ? <Button size="sm" className="h-7" onClick={() => run(() => api.patch(`/zoe/memoire/${r.id}`, { texte }), "Règle modifiée").then(() => setEdit(false))} data-testid={`memoire-edit-save-${r.id}`}>OK</Button>
        : <button type="button" onClick={() => setEdit(true)} className="text-slate-400 hover:text-slate-800" data-testid={`memoire-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>)}
      {editable && <button type="button" onClick={() => window.confirm("Supprimer cette règle de la mémoire de ZOE ?") && run(() => api.delete(`/zoe/memoire/${r.id}`), "Règle supprimée")}
        className="text-slate-400 hover:text-red-600" data-testid={`memoire-supprimer-${r.id}`}><Trash2 className="h-3.5 w-3.5" /></button>}
    </div>
  );
}

function Section({ titre, items, vide, testId, ...p }) {
  return (
    <section data-testid={testId}>
      <h2 className="text-sm font-semibold text-slate-900 mb-2">{titre} <span className="text-slate-400 font-normal">({items.length})</span></h2>
      <div className="space-y-2">{items.length ? items.map((r) => <Regle key={r.id} r={r} {...p} />) : <p className="text-xs text-slate-400">{vide}</p>}</div>
    </section>
  );
}

export default function MemoireZoe() {
  const [d, setD] = useState(null);
  const [texte, setTexte] = useState("");
  const [portee, setPortee] = useState("perso");
  const load = useCallback(async () => {
    try { setD((await api.get("/zoe/memoire")).data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const ajouter = async () => {
    try {
      const { data } = await api.post("/zoe/memoire", { texte, portee, source: "manuel" });
      toast.success(data.statut === "a_valider" ? "Proposée au GE : en attente de validation" : "Règle ajoutée à la mémoire de ZOE");
      setTexte(""); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  if (!d) return <div className="text-sm text-slate-500">Chargement…</div>;
  return (
    <div className="space-y-6 max-w-4xl" data-testid="memoire-page">
      <PageHeader title="Mémoire de ZOE" subtitle="Ce que ZOE a appris : relu avant chaque réponse. Rien n'est retenu sans accord ; les règles du GE sont validées par un admin ou un gestionnaire." />
      <div className="flex flex-wrap items-center gap-3 text-xs text-slate-600" data-testid="memoire-notes">
        <Brain className="h-4 w-4 text-[#A88846]" />Vos notes : <span className="inline-flex items-center gap-1"><ThumbsUp className="h-3 w-3" />{d.notes.positives || 0}</span>
        <span className="inline-flex items-center gap-1"><ThumbsDown className="h-3 w-3" />{d.notes.negatives || 0}</span>
        <span className="text-slate-400">— les réponses notées servent d'exemples ou de contre-exemples.</span>
      </div>
      <div className="flex flex-wrap gap-2 items-center rounded-xl border border-slate-200 bg-white p-3">
        <Input value={texte} onChange={(e) => setTexte(e.target.value)} placeholder="Ex. : Nos factures sont payables à 30 jours fin de mois" className="flex-1 min-w-[280px]" data-testid="memoire-ajout-texte" />
        <select value={portee} onChange={(e) => setPortee(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="memoire-ajout-portee">
          <option value="perso">Pour moi</option><option value="ge">Pour tout le GE</option>
        </select>
        <Button onClick={ajouter} disabled={texte.trim().length < 3} className="bg-[#C5A059] hover:bg-[#A88846] text-white" data-testid="memoire-ajout-btn"><Plus className="h-4 w-4 mr-1" />Ajouter</Button>
      </div>
      {d.a_valider.length > 0 && <Section titre={d.peut_valider ? "Propositions à valider pour le GE" : "Vos propositions en attente"} items={d.a_valider}
        vide="" testId="memoire-a-valider" validable={d.peut_valider} editable onChange={load} />}
      <Section titre="Règles du GE" items={d.ge} vide="Aucune règle partagée pour l'instant." testId="memoire-ge" editable={d.peut_valider} onChange={load} />
      <Section titre="Mes préférences" items={d.perso} vide="Aucune préférence. Dites par exemple à ZOE : « retiens que je veux des réponses courtes »." testId="memoire-perso" editable onChange={load} />
    </div>
  );
}
